import { Inject, Injectable } from '@angular/core';
import { LogEntry } from '@timechef/core/logging/log-entry';
import { Sink } from '@timechef/core/logging/sinks';
import { LogLevel } from 'src/environments/environment-config';
import { clearCircularFromObject } from '../utils/json-utils';

export interface ILogger {
  debug(message?: any, ...optionalParams: any[]): void;
  info(message?: any, ...optionalParams: any[]): void;
  warn(message?: any, ...optionalParams: any[]): void;
  error(message?: any, ...optionalParams: any[]): void;
  // TODO Conserver le niveau Fatal ???
  fatal(message?: any, ...optionalParams: any[]): void;
}

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private activeSinks: Sink[];

  constructor(@Inject(Sink) sinks: Sink[]) {
    this.activeSinks = sinks.filter((x) => x.config.isActive);
  }

  /**
   * Get a logger, scoped if scope is given
   * @param scope the scope (ie: the service), should be a class, do not use interface
   * @returns scopped ILogger
   * @example getLogger(MyService)
   */
  public getLogger(scope?: string): ILogger {
    return new LogService(scope || '', this.activeSinks);
  }
}

class LogService implements ILogger {
  constructor(
    private scope: string,
    private sinks: Sink[],
  ) {}

  debug(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Debug, optionalParams);
  }

  info(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Info, optionalParams);
  }

  warn(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Warn, optionalParams);
  }

  error(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Error, optionalParams);
  }

  fatal(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Fatal, optionalParams);
  }

  private writeToLog(msg: string, level: LogLevel, params: any[]) {
    // TODO Get sink's min level to avoid create an entry for nothing
    const entry: LogEntry = new LogEntry();
    entry.scope = this.scope;
    entry.message = msg;
    entry.logLevel = level;
    entry.extraInfo = params.map((x) => {
      // Clearing potentiel circular reference from extra infos (errors)
      if (typeof x === 'object') return clearCircularFromObject(x);

      return x;
    });

    for (const sink of this.sinks) {
      if (shouldLog(entry.logLevel, sink.config.logLevel)) {
        sink.log(entry).subscribe();
      }
    }
  }
}

const shouldLog = (log: LogLevel, allowed: LogLevel) => {
  const keys = Object.keys(LogLevel);
  return keys.indexOf(log) <= keys.indexOf(allowed);
};
